import { Link } from "react-router-dom";
import { Heart, MapPin } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

export const EventCard = ({ event, onChange, index = 0 }) => {
  const { user, login } = useAuth();

  const toggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast("Sign in to save events");
      login();
      return;
    }
    const res = await apiFetch(`/events/${event.event_id}/save`, { method: "POST" });
    onChange?.({ ...event, saved: res.saved });
    toast(res.saved ? "Saved to your list" : "Removed from saved");
  };

  return (
    <Link
      to={`/events/${event.event_id}`}
      data-testid={`event-card-${event.event_id}`}
      className="group block fade-up"
      style={{ animationDelay: `${Math.min(index, 8) * 55}ms` }}
    >
      <div className="relative overflow-hidden rounded-2xl bg-neutral-100 aspect-video">
        <img
          src={event.image_url}
          alt={event.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        />
        <button
          onClick={toggleSave}
          data-testid={`save-btn-${event.event_id}`}
          aria-label="Save event"
          className="absolute right-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-white/85 backdrop-blur-md transition-colors duration-200 hover:bg-white"
        >
          <Heart
            className={`h-[18px] w-[18px] transition-colors duration-200 ${
              event.saved ? "fill-[#84CC16] text-[#4D7C0F]" : "text-neutral-700"
            }`}
          />
        </button>
        <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-neutral-800 backdrop-blur-md">
          {event.category}
        </span>
      </div>

      <div className="pt-3">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-[15px] font-semibold leading-snug text-neutral-900">{event.title}</h3>
          <span className="whitespace-nowrap text-sm font-semibold text-neutral-900">{event.price}</span>
        </div>
        <p className="mt-1 text-sm text-neutral-500">{fmt(event.start_at)}</p>
        <p className="mt-0.5 flex items-center gap-1 text-sm text-neutral-500">
          <MapPin className="h-3.5 w-3.5" /> {event.venue_name} · {event.neighborhood}
        </p>
        <p className="mt-1 text-xs font-medium text-neutral-400">
          {event.rsvp_count} going · hosted by {event.host_org || event.host_name}
        </p>
      </div>
    </Link>
  );
};
